'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { AuthChangeEvent, Session, SupabaseClient, User } from '@supabase/supabase-js';
import { createClient } from '../lib/supabase/client';
import { identifyUser, resetUser } from '../lib/mixpanel';
import type { UserProfile } from '../lib/supabase/services';

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  isPM: boolean;
  isStageManager: boolean;
  isDirector: boolean;
  isContractor: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isAdminOrAbove: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

function buildFallbackProfile(nextUser: User, currentProfile: UserProfile | null): UserProfile | null {
  const fallbackRole = nextUser.user_metadata?.role;

  if (currentProfile?.id === nextUser.id) {
    return currentProfile;
  }

  if (!fallbackRole) {
    return null;
  }

  return {
    id: nextUser.id,
    email: nextUser.email ?? currentProfile?.email ?? '',
    full_name: nextUser.user_metadata?.full_name ?? currentProfile?.full_name ?? '',
    avatar_url: nextUser.user_metadata?.avatar_url ?? currentProfile?.avatar_url ?? '',
    user_role: fallbackRole,
    company: currentProfile?.company ?? '',
    is_active: currentProfile?.is_active ?? true,
  } as UserProfile;
}

// Fetch profile using the already-authenticated client so we never open a
// second connection or trigger a lock conflict.
async function fetchProfile(
  supabase: SupabaseClient,
  userId: string
): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) return null;
  return data;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);
  const claimedForRef = useRef<string | null>(null);
  const supabase = createClient();

  const loadProfile = async (nextUser: User) => {
    try {
      const dbProfile = await fetchProfile(supabase, nextUser.id);
      if (dbProfile) {
        setProfile(dbProfile);
        return dbProfile;
      }
    } catch {
      // Fall through to fallback below.
    }

    // DB returned nothing (transient error, RLS, or missing row).
    // If we already have a valid profile for this exact user — e.g. during a
    // TOKEN_REFRESHED cycle — keep it rather than wiping it with null, which
    // would blank out the UI. Use the functional-update form of setProfile so
    // we always read the *current* state, not a stale closure value.
    setProfile((current) => {
      if (current?.id === nextUser.id) return current;
      return buildFallbackProfile(nextUser, current);
    });
  };

  // Load (or clear) the profile whenever the signed-in user changes.
  // This effect runs OUTSIDE the supabase-js Web Lock, so supabase.from()
  // calls inside loadProfile() are safe. Never call supabase.from() inside
  // onAuthStateChange: in supabase-js v2.39+, INITIAL_SESSION, USER_UPDATED,
  // and TOKEN_REFRESHED all fire while the lock is held. A DB query inside
  // those callbacks calls getSession() which tries to re-enter the same lock,
  // creating a circular wait that deadlocks the entire client forever.
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    loadProfile(user);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once per signed-in user: claim any unclaimed contractor row matching this
  // person's verified email, so an admin-created contractor tied to a login is
  // never stranded (the permanent fix for the recurring onboarding-link gap).
  // Best-effort — failure is silent and a page-level resolve still works.
  useEffect(() => {
    const uid = user?.id;
    if (!uid || claimedForRef.current === uid) return;
    claimedForRef.current = uid;
    supabase.rpc('claim_my_contractor').then(
      () => {},
      () => {},
    );
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!isMounted) return;

      setSession(session);
      setUser(session?.user ?? null);
      currentUserIdRef.current = session?.user?.id ?? null;

      if (session?.user) {
        identifyUser(session.user.id, {
          $email: session.user.email,
          $name: session.user.user_metadata?.full_name || '',
        });
      } else {
        setProfile(null);
      }

      if (isMounted) {
        initializedRef.current = true;
        setLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      if (!isMounted) return;

      const nextUserId = session?.user?.id ?? null;
      const userChanged = currentUserIdRef.current !== nextUserId;
      const shouldBlockUi = !initializedRef.current || userChanged;

      if (shouldBlockUi) {
        setLoading(true);
      }

      // Always update session so session.access_token stays current.
      setSession(session);

      // Only replace the user object when the actual user changed (sign-in,
      // sign-out, or user switch). TOKEN_REFRESHED produces a new object with
      // the same ID — updating user state would cause every component with
      // [user] in its useEffect deps to clear and re-fetch data unnecessarily.
      if (userChanged) {
        setUser(session?.user ?? null);
      }
      currentUserIdRef.current = nextUserId;

      if (session?.user) {
        identifyUser(session.user.id, {
          $email: session.user.email,
          $name: session.user.user_metadata?.full_name || '',
        });
      } else if (userChanged) {
        setProfile(null);
      }

      if (isMounted) {
        initializedRef.current = true;
        if (shouldBlockUi) {
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'contractor',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    if (data.user) {
      identifyUser(data.user.id, {
        $email: email,
        $name: metadata?.fullName || '',
        signup_method: 'email',
      });
    }
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    if (data.user) {
      identifyUser(data.user.id, {
        $email: data.user.email,
        $name: data.user.user_metadata?.full_name || '',
        last_login: new Date().toISOString(),
      });
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    resetUser();
    currentUserIdRef.current = null;
    setSession(null);
    setUser(null);
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfileFn = async (): Promise<UserProfile | null> => {
    if (profile?.id === user?.id) {
      return profile;
    }
    if (!user) return null;
    return fetchProfile(supabase, user.id);
  };

  // Priority: JWT app_metadata (instant, no DB) → DB profile → legacy user_metadata
  const resolvedRole = user?.app_metadata?.user_role ?? profile?.user_role ?? user?.user_metadata?.role ?? null;
  const isPM = resolvedRole === 'production-manager' || resolvedRole === 'director' || resolvedRole === 'admin' || resolvedRole === 'super-admin';
  const isStageManager = resolvedRole === 'stage-manager';
  const isDirector = resolvedRole === 'director';
  const isContractor = resolvedRole === 'contractor';
  const isAdmin = resolvedRole === 'admin' || resolvedRole === 'super-admin' || resolvedRole === 'director';
  const isSuperAdmin = resolvedRole === 'super-admin';
  const isAdminOrAbove = resolvedRole === 'admin' || resolvedRole === 'super-admin' || resolvedRole === 'director';

  // Expose loading as "session AND profile resolved" instead of just "session
  // resolved". Pages that gate on role (e.g., `if (!authLoading && !isPM)
  // redirect`) were misfiring during the gap between user-loaded and
  // profile-loaded: resolvedRole fell back to user_metadata (often blank or
  // 'contractor'), isPM was false, page redirected, then profile loaded a
  // moment later with the real role — too late. We block the loading flag
  // until the profile is in hand whenever a user is signed in. JWT-only paths
  // (app_metadata.user_role present) short-circuit since we don't need the
  // DB row to know the role.
  const consumerLoading =
    loading ||
    (!!user && !user?.app_metadata?.user_role && profile === null);

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading: consumerLoading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile: getUserProfileFn,
    isPM,
    isStageManager,
    isDirector,
    isContractor,
    isAdmin,
    isSuperAdmin,
    isAdminOrAbove,
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:331:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="331" data-component-end-line="331" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
